import React from 'react';
import styled from 'styled-components';
import Link from 'components/core/link';
import { useQuery } from '@apollo/client';

import { GetCollectionsNavigationDocument } from 'src/gql/graphql';
import type { GetCollectionsNavigationQuery } from 'src/gql/graphql';
import ROUTES from 'src/routes';
import usePaths from 'src/hooks/use-paths';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { tracker } from 'src/utils/analytics';
import { LoginCta } from 'src/components/ctas';
import { getCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';
import { useCollectionChannel } from 'src/hooks/use-collection-channel';

type ShopTabProps = {
  onClose: () => void;
};

export const ShopTab = ({ onClose }: ShopTabProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const channel = useCollectionChannel();
  const { href: baseHref } = usePaths();
  const { COLLECTIONS } = ROUTES;

  const handleLinkClick = (label: string): void => {
    tracker.gaGTMClickEvent({
      eventCategory: `navigation click`,
      eventAction: `main menu`,
      eventLabel: label,
    });

    onClose();
  };

  const { data } = useQuery(GetCollectionsNavigationDocument, {
    skip: !dispensary?.id,
    variables: {
      dispensaryId: dispensary.id,
      customCategory: false,
      enabled: true,
      activeOnly: true,
      ...(channel && { channel }),
    },
  });

  const collectionNavItems: NonNullable<GetCollectionsNavigationQuery['getCollectionsNavigation']> =
    data?.getCollectionsNavigation ?? [];
  const collections = useActiveScheduledItems({
    items: collectionNavItems,
    getSchedule: (collection) => collection.schedule,
    dispensaryTimezone: dispensary?.timezone,
    enablePolling: true,
  });

  return (
    <Container>
      <Nav>
        <div>
          <NavHeading>Categories</NavHeading>
          <NavList>
            {categories.map(({ key, label, slug, customCategory }) => {
              const href = getCustomCategoryLink(baseHref, customCategory, slug, key);
              if (!href) {
                return null;
              }
              return (
                <li key={key}>
                  <NavLink
                    href={href}
                    onClick={() => handleLinkClick(`categories > ${String(label)}`)}
                    data-testid='menu-drawer-category-link'
                  >
                    {label}
                  </NavLink>
                </li>
              );
            })}
          </NavList>
        </div>

        {collections.length > 0 && (
          <div>
            <NavHeading>Collections</NavHeading>
            <NavList>
              {collections.map(({ slug, navigationTitle, title }) => (
                <li key={slug}>
                  <NavLink
                    href={`${baseHref}${COLLECTIONS}${slug}`}
                    onClick={() => handleLinkClick(`collections > ${navigationTitle ?? title}`)}
                  >
                    {navigationTitle ?? title}
                  </NavLink>
                </li>
              ))}
            </NavList>
          </div>
        )}
      </Nav>

      <LoginCta />
    </Container>
  );
};

const Container = styled.div`
  padding: var(--dcm-drawer-nav-padding);
  max-height: 100%;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: var(--dcm-drawer-nav-gap);
  margin-bottom: var(--dcm-drawer-nav-gap);
`;

const NavHeading = styled.h2`
  color: var(--dcm-drawer-nav-heading-color);
  font-size: var(--dcm-drawer-nav-heading-font-size);
  font-weight: var(--dcm-drawer-nav-heading-font-weight);
  line-height: 1;
  margin: var(--dcm-drawer-nav-gap) 0;
`;

const NavList = styled.ul`
  font-size: var(--dcm-drawer-nav-link-font-size);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavLink = styled(Link)`
  color: var(--dcm-drawer-nav-link-color);
  display: block;
  font-size: var(--dcm-drawer-nav-link-font-size);
  line-height: 1;
  padding: var(--dcm-drawer-nav-link-padding);
`;
